import { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'error' | 'warning';
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { ...toast, id }]);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function Toaster() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Simple global toast state
  if (typeof window !== 'undefined') {
    (window as unknown as { __addToast: (toast: Omit<Toast, 'id'>) => void }).__addToast = (
      toast: Omit<Toast, 'id'>
    ) => {
      const id = Math.random().toString(36).substring(7);
      setToasts((prev) => [...prev, { ...toast, id }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 5000);
    };
  }

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {toasts.map((toast) => (
        <ToastPrimitive.Root
          key={toast.id}
          className={cn(
            'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 shadow-lg transition-all',
            'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
            'data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-out',
            {
              'border-gray-200 bg-white': toast.variant === 'default' || !toast.variant,
              'border-success/20 bg-success/10': toast.variant === 'success',
              'border-error/20 bg-error/10': toast.variant === 'error',
              'border-warning/20 bg-warning/10': toast.variant === 'warning',
            }
          )}
          onOpenChange={(open) => {
            if (!open) setToasts((prev) => prev.filter((t) => t.id !== toast.id));
          }}
        >
          <div className="grid gap-1">
            <ToastPrimitive.Title className="text-sm font-semibold">
              {toast.title}
            </ToastPrimitive.Title>
            {toast.description && (
              <ToastPrimitive.Description className="text-sm text-gray-500">
                {toast.description}
              </ToastPrimitive.Description>
            )}
          </div>
          <ToastPrimitive.Close className="absolute right-2 top-2 rounded-sm opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100 focus:opacity-100">
            <X className="h-4 w-4" />
          </ToastPrimitive.Close>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-4 sm:right-4 sm:flex-col sm:max-w-[420px]" />
    </ToastPrimitive.Provider>
  );
}

export function toast(options: Omit<Toast, 'id'>) {
  if (typeof window !== 'undefined') {
    (window as unknown as { __addToast: (toast: Omit<Toast, 'id'>) => void }).__addToast?.(options);
  }
}
